import SvgChevron from '@app/shared/icons/Chevron';
import { useEffect, useState } from 'react';
import styles from './valuation-conclusion.module.scss';
import {
  OpmValuationConclusionInstrumentResultDto,
  OpmValuationConclusionInstrumentYearResultDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import { slugifyStrings } from '@app/shared/helpers/formatting/slugify';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { Alignment } from '@app/shared/enums/alignment.enum';
import {
  amountRules,
  decimalCalculatedViewRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters';

interface ValuationConclusionProps {
  instrument: OpmValuationConclusionInstrumentResultDto;
  isCollapsed: boolean;
  allRowsCollapsed: boolean;
  sortedValuedInstruments: OpmValuationConclusionInstrumentResultDto[];
  onChildRowStateChange: (isRowCollapsed: boolean) => void;
}

const ValuationInstrument = ({
  instrument,
  isCollapsed,
  allRowsCollapsed,
  sortedValuedInstruments,
  onChildRowStateChange,
}: ValuationConclusionProps): JSX.Element => {
  const [isRowCollapsed, setIsRowCollapsed] = useState<boolean>(isCollapsed);

  useEffect(() => {
    setIsRowCollapsed(isCollapsed);
  }, [isCollapsed]);

  const toggleCollapse = () => {
    const newIsRowCollapsed = !isRowCollapsed;
    setIsRowCollapsed(newIsRowCollapsed);
    onChildRowStateChange(newIsRowCollapsed);
  };

  const getWeightedValue = (
    instrumentId: string,
    property: keyof OpmValuationConclusionInstrumentYearResultDto
  ) => {
    const instrumentWeightedValues = sortedValuedInstruments.find(
      (valuedInstrument) => valuedInstrument.instrumentId === instrumentId
    )?.weightedAverage;

    const value = instrumentWeightedValues ? instrumentWeightedValues[property] : 0;

    return typeof value === 'number' ? value : 0;
  };

  return (
    <>
      <tr key={instrument.instrumentId} onClick={toggleCollapse}>
        <td
          className={styles['opm-instrument-header']}
          data-testid={slugifyStrings(instrument.instrumentNarrative, 'title')}>
          <div
            className={styles['expand-collapse-chevron']}
            data-testid={slugifyStrings(instrument.instrumentNarrative, 'expand-button')}>
            {isRowCollapsed ? (
              <SvgChevron onClick={toggleCollapse} className={styles[`opm-chevron-collapsed`]} />
            ) : (
              <SvgChevron onClick={toggleCollapse} className={styles[`opm-chevron-expanded`]} />
            )}
          </div>
          <CellValue
            value={instrument.instrumentNarrative}
            className={
              allRowsCollapsed
                ? styles[`opm-valuation-header-collapsed`]
                : styles[`opm-valuation-header-expanded`]
            }
            alignment={Alignment.Right}
          />
        </td>
        <td />
        <td>
          <CellValue
            value={getWeightedValue(instrument.instrumentId, 'presentValue')}
            alignment={Alignment.Right}
            className={styles[`opm-valuation-value`]}
            {...amountRules}
            dataTestId={slugifyStrings(instrument.instrumentNarrative, 'avg-present-value')}
          />
        </td>
        <td>
          <CellValue
            value={getCalculatedProjectValueInPercent(
              getWeightedValue(instrument.instrumentId, 'minorityDiscounts')
            )}
            className={styles[`opm-valuation-value`]}
            {...enteredPercentageViewRules}
            dataTestId={slugifyStrings(instrument.instrumentNarrative, 'avg-minority-discounts')}
          />
        </td>
        <td>
          <CellValue
            value={getWeightedValue(instrument.instrumentId, 'marketValue')}
            alignment={Alignment.Right}
            className={styles[`opm-valuation-value`]}
            {...amountRules}
            dataTestId={slugifyStrings(instrument.instrumentNarrative, 'avg-market-value')}
          />
        </td>
        <td>
          <CellValue
            value={getWeightedValue(instrument.instrumentId, 'marketValuePerShare')}
            alignment={Alignment.Right}
            className={styles[`opm-valuation-value`]}
            {...decimalCalculatedViewRules}
            dataTestId={slugifyStrings(
              instrument.instrumentNarrative,
              'avg-market-value-per-share'
            )}
          />
        </td>
      </tr>
      {!isRowCollapsed &&
        instrument.years.map((value, index) => (
          <tr key={index}>
            <td className={styles['pl-45']}>
              <CellValue
                value={new Date(value.date.toString()).toLocaleDateString('en-US', {
                  month: 'short',
                  year: '2-digit',
                })}
                className={styles[`opm-valuation-expanded-value`]}
                dataTestId={slugifyStrings(instrument.instrumentNarrative, value.date)}
              />
            </td>
            <td>
              <CellValue
                value={getCalculatedProjectValueInPercent(value.exitProbability)}
                className={styles[`opm-valuation-expanded-value`]}
                {...enteredPercentageViewRules}
                dataTestId={slugifyStrings(
                  instrument.instrumentNarrative,
                  value.date,
                  'exit-probability'
                )}
              />
            </td>
            <td>
              <CellValue
                value={value.presentValue}
                className={styles[`opm-valuation-expanded-value`]}
                alignment={Alignment.Right}
                {...amountRules}
                dataTestId={slugifyStrings(
                  instrument.instrumentNarrative,
                  value.date,
                  'present-value'
                )}
              />
            </td>
            <td>
              <CellValue
                value={getCalculatedProjectValueInPercent(value.minorityDiscounts)}
                className={styles[`opm-valuation-expanded-value`]}
                {...enteredPercentageViewRules}
                dataTestId={slugifyStrings(
                  instrument.instrumentNarrative,
                  value.date,
                  'minority-discounts'
                )}
              />
            </td>
            <td>
              <CellValue
                value={value.marketValue}
                className={styles[`opm-valuation-expanded-value`]}
                alignment={Alignment.Right}
                {...amountRules}
                dataTestId={slugifyStrings(
                  instrument.instrumentNarrative,
                  value.date,
                  'market-value'
                )}
              />
            </td>
            <td>
              <CellValue
                value={Number(value.marketValuePerShare)}
                className={styles[`opm-valuation-expanded-value`]}
                alignment={Alignment.Right}
                {...decimalCalculatedViewRules}
                dataTestId={slugifyStrings(
                  instrument.instrumentNarrative,
                  value.date,
                  'market-value-per-share'
                )}
              />
            </td>
          </tr>
        ))}
    </>
  );
};

export default ValuationInstrument;
